import React from 'react';
import parse, { domToReact, Element, DOMNode } from 'html-react-parser';
import {
    Typography,
    TypographyAppearanceVariants,
    TypographyTagVariants,
} from '@components/atoms/Typography';
import type { TypographyProps } from '@components/atoms/Typography';

export const useHtmlTransformer = () => {
    return {
        transformHtml: (htmlString: string) => {
            return (
                <div>
                    {parse(htmlString, {
                        replace: (domNode: DOMNode) => {
                            return transformElement(domNode as Element);
                        },
                    })}
                </div>
            );
        },
    };
};

type TypographyFontWeight = NonNullable<TypographyProps['fontWeight']>;

const TYPOGRAPHY_FONT_WEIGHT_VALUES: ReadonlyArray<TypographyFontWeight> = ['300', '400', '700', '800', '900'];
const TYPOGRAPHY_FONT_WEIGHT_SET = new Set<TypographyFontWeight>(TYPOGRAPHY_FONT_WEIGHT_VALUES);

const FONT_WEIGHT_KEYWORD_MAP: Record<string, TypographyFontWeight> = {
    normal: '400',
    bold: '700',
    bolder: '800',
    lighter: '300',
};

const extractTypographyStyle = (style?: React.CSSProperties) => {
    if (!style) {
        return { style: undefined, fontWeight: undefined as TypographyProps['fontWeight'] };
    }

    const clonedStyle: React.CSSProperties = { ...style };
    const rawFontWeight = clonedStyle.fontWeight;
    let extractedFontWeight: TypographyProps['fontWeight'];

    if (rawFontWeight !== undefined) {
        const { fontWeight, inlineFontWeight } = parseFontWeightFromInlineStyle(rawFontWeight);

        if (fontWeight) {
            extractedFontWeight = fontWeight;
        }

        if (inlineFontWeight === undefined) {
            delete clonedStyle.fontWeight;
        } else {
            clonedStyle.fontWeight = inlineFontWeight;
        }
    }

    return {
        style: Object.keys(clonedStyle).length > 0 ? clonedStyle : undefined,
        fontWeight: extractedFontWeight,
    };
};

const parseFontWeightFromInlineStyle = (
    value: React.CSSProperties['fontWeight'],
): {
    fontWeight?: TypographyFontWeight;
    inlineFontWeight?: React.CSSProperties['fontWeight'];
} => {
    if (value === undefined) {
        return {};
    }

    const normalizedValue =
        typeof value === 'number'
            ? value.toString()
            : value
                  .toString()
                  .replace(/!important/gi, '')
                  .trim();

    if (!normalizedValue) {
        return { inlineFontWeight: undefined };
    }

    const keywordMatch = FONT_WEIGHT_KEYWORD_MAP[normalizedValue.toLowerCase()];

    if (keywordMatch) {
        return { fontWeight: keywordMatch, inlineFontWeight: undefined };
    }

    if (/^\d+$/.test(normalizedValue)) {
        const numericWeight = normalizedValue;
        const typographyWeight = TYPOGRAPHY_FONT_WEIGHT_SET.has(numericWeight as TypographyFontWeight)
            ? (numericWeight as TypographyFontWeight)
            : undefined;

        if (typographyWeight) {
            return { fontWeight: typographyWeight, inlineFontWeight: undefined };
        }

        const numericValue = Number(numericWeight);

        if (!Number.isNaN(numericValue)) {
            return { inlineFontWeight: numericValue };
        }
    }

    return { inlineFontWeight: normalizedValue as React.CSSProperties['fontWeight'] };
};

const transformElement = (domNode: Element) => {
    const domAttributes = domNode.attribs || {};
    const inlineStyle = mapStyleAttribute(domAttributes.style);
    const { style: typographyInlineStyle, fontWeight } = extractTypographyStyle(inlineStyle);
    const domClasses = domAttributes.class;
    const children = domNode.children ? domToReact(domNode.children as DOMNode[]) : null;

    switch (domNode.name) {
        case 'h1':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h1}
                    appearanceLevel={TypographyAppearanceVariants.level2}
                    classes={mergeClasses('mb-3', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'h2':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h2}
                    appearanceLevel={TypographyAppearanceVariants.level3}
                    classes={mergeClasses('pt-2 mb-2', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'h3':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h3}
                    appearanceLevel={TypographyAppearanceVariants.level4}
                    classes={mergeClasses('pt-2 mb-2', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'h4':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h4}
                    appearanceLevel={TypographyAppearanceVariants.level5}
                    classes={mergeClasses('pt-2 mb-2', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'h5':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h5}
                    appearanceLevel={TypographyAppearanceVariants.level7}
                    classes={mergeClasses('pt-2 mb-2', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'h6':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.h6}
                    appearanceLevel={TypographyAppearanceVariants.level8}
                    classes={mergeClasses('pt-2 mb-2', domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        case 'p':
            return (
                <Typography
                    tagVariant={TypographyTagVariants.p}
                    color="muted"
                    classes={mergeClasses(undefined, domClasses)}
                    fontWeight={fontWeight}
                    style={typographyInlineStyle}>
                    {children ? children : <></>}
                </Typography>
            );
        default:
            return domNode;
    }
};

const mergeClasses = (baseClasses: string | undefined, domClasses?: string) => {
    if (!baseClasses && !domClasses) {
        return undefined;
    }

    return [baseClasses, domClasses]
        .filter((value) => Boolean(value && value.trim().length > 0))
        .join(' ')
        .trim();
};

const mapStyleAttribute = (style?: string) => {
    if (!style) {
        return undefined;
    }

    const result: React.CSSProperties = {};

    style
        .split(';')
        .map((declaration) => declaration.trim())
        .filter((declaration) => declaration.length > 0)
        .forEach((declaration) => {
            const [rawProperty, ...rawValueParts] = declaration.split(':');

            if (!rawProperty || rawValueParts.length === 0) {
                return;
            }

            const property = rawProperty.trim();
            const value = rawValueParts.join(':').trim();

            if (!property || !value) {
                return;
            }

            const parsedProperty = property.startsWith('--')
                ? property
                : property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

            (result as Record<string, React.CSSProperties[keyof React.CSSProperties]>)[parsedProperty] =
                value as React.CSSProperties[keyof React.CSSProperties];
        });

    return Object.keys(result).length > 0 ? result : undefined;
};
